import React, { useState, useRef, useEffect, useCallback } from 'react';
import { theme } from './theme';
import { getTabMenuStyles } from './styleUtils';

export interface TabItem {
	title: string;
	content: React.ReactNode;
	disabled?: boolean;
	badgeCount?: number;
	badgeVisible?: boolean;
}

export interface TabMenuProps {
	tabs: TabItem[];
	maxWidth?: number;
	activeTabIndex?: number;
	// eslint-disable-next-line no-unused-vars
	onTabChanged?: (index: number) => void;
}

export const TabMenu: React.FC<TabMenuProps> = ({
	tabs,
	maxWidth,
	activeTabIndex,
	onTabChanged
}) => {
	const [activeTab, setActiveTab] = useState(activeTabIndex || 0);
	const [isOverflowing, setIsOverflowing] = useState(false);
	const tabHeaderRef = useRef<HTMLDivElement>(null);
	const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
	const styles = getTabMenuStyles(theme);

	useEffect(() => {
		const checkOverflow = () => {
			if (tabHeaderRef.current && maxWidth) {
				const isWidthOverflowing = tabHeaderRef.current.scrollWidth > maxWidth;
				setIsOverflowing(isWidthOverflowing);
			}
		};
		checkOverflow();
		window.addEventListener('resize', checkOverflow);
		return () => window.removeEventListener('resize', checkOverflow);
	}, [tabs, maxWidth]);

	const handleTabClick = (index: number) => {
		if (!tabs[index].disabled) {
			setActiveTab(index);
			tabRefs.current[index]?.focus();
		}
		if (onTabChanged) {
			onTabChanged(index);
		}
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
		if (event.key === 'ArrowRight') {
			event.preventDefault();
			const nextIndex = (index + 1) % tabs.length;
			if (!tabs[nextIndex].disabled) {
				handleTabClick(nextIndex);
			}
		} else if (event.key === 'ArrowLeft') {
			event.preventDefault();
			const prevIndex = (index - 1 + tabs.length) % tabs.length;
			if (!tabs[prevIndex].disabled) {
				handleTabClick(prevIndex);
			}
		} else if (event.key === 'Home') {
			event.preventDefault();
			handleTabClick(0);
		} else if (event.key === 'End') {
			event.preventDefault();
			handleTabClick(tabs.length - 1);
		}
	};
	const setTabRef = useCallback((el: HTMLButtonElement | null, index: number) => {
		tabRefs.current[index] = el;
	}, []);

	return (
		<div style={{ ...styles.tabMenu_css, maxWidth: maxWidth || 'none' }}>
			<div
				ref={tabHeaderRef}
				style={{
					...styles.tabHeader_css,
					overflowX: isOverflowing ? 'scroll' : 'hidden',
					overflowY: 'hidden',
					flexWrap: 'nowrap',
					width: '100%'
				}}
				role="tablist"
			>
				{tabs.map((tab, index) => (
					<button
						key={index}
						id={`tab-${index}`}
						ref={(el) => setTabRef(el, index)}
						style={{
							...styles.tabButton_css,
							...(activeTab === index ? styles.tabButtonActive_css : {}),
							...(tab.disabled ? styles.tabButtonDisabled_css : {}),
							flexShrink: 0,
							position: 'relative'
						}}
						onClick={() => handleTabClick(index)}
						onKeyDown={(e) => handleKeyDown(e, index)}
						disabled={tab.disabled}
						role="tab"
						aria-controls={`tab-content-${index}`}
						aria-selected={activeTab === index}
						tabIndex={activeTab === index ? 0 : -1}
					>
						{tab.title}
						{tab.badgeVisible && tab.badgeCount !== undefined && (
							<span style={styles.badge_css}>{tab.badgeCount}</span>
						)}
						{activeTab === index && !tab.disabled && <span style={styles.activeIndicator_css} />}
					</button>
				))}
			</div>
			{tabs.map((tab, index) => (
				<div
					key={index}
					id={`tab-content-${index}`}
					role="tabpanel"
					aria-labelledby={`tab-${index}`}
					hidden={activeTab !== index}
					style={styles.tabContent_css}
				>
					{tab.content}
				</div>
			))}
		</div>
	);
};
